import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { IonIcon } from '@ionic/react';
import { Helmet } from 'react-helmet-async';
import { paperPlaneOutline } from 'ionicons/icons';
import { useToast } from 'contexts/ToastContext';
import ContactService from 'services/contact';
import AuthService from '../../services/auth';
import './ContactPage.css';

const ContactPage = () => {
  const location = useLocation();
  const [, setUser] = useState(null);

  const prefill = location.state?.prefill;
  const [formData, setFormData] = useState({
    subject: prefill === 'enterprise' ? 'Enterprise Plan Inquiry' : '',
    message: prefill === 'enterprise' 
      ? 'Hi, I\'m interested in the Enterprise plan.\n\nCompany name: \nCompany size: \nContact person: \nContact number: \nExpected monthly analyses: \nAnything else: ' 
      : '',
    priority: prefill === 'enterprise' ? 'medium' : 'medium'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get user info
  useEffect(() => {
    if (AuthService.isAuthenticated()) {
      const currentUser = AuthService.getCurrentUser();
      setUser(currentUser);
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Contact Us - Zibby</title>
        <meta name="description" content="Get in touch with the Zibby team for support and inquiries." />
      </Helmet>
      <ContactPageContent 
        formData={formData}
        setFormData={setFormData}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
      />
    </>
  );
};

const ContactPageContent = ({ formData, setFormData, isSubmitting, setIsSubmitting }) => {
  const { showToast } = useToast();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Submit the message via API
      const response = await ContactService.submitMessage(formData);
      
      showToast(
        response.message || 'Message sent successfully! We\'ll get back to you within 24 hours.',
        'success'
      );
      
      // Reset form
      setFormData({
        subject: '',
        message: '',
        priority: 'medium'
      });
      
    } catch (error) {
      console.error('Contact form submission error:', error);
      showToast(
        error.message || 'Failed to send your message. Please try again or email us directly.',
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page page-container-md page-fade-in main-body">
        <div className="page-header-center">
          <h1>Contact Us</h1>
          <p>We'll get back to you as soon as possible</p>
        </div>

        <div className="page-layout main-body">
          <div className="contact-form-section">
            <div className="page-card">
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="subject" className="form-label">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="What can we help you with?"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="priority" className="form-label">Priority</label>
                  <select
                    id="priority"
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="low">Low - General question</option>
                    <option value="medium">Medium - Need assistance</option>
                    <option value="high">High - Urgent issue</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="form-input form-textarea"
                    placeholder="Please describe your question or issue in detail..."
                    rows="12"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-full-width"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="spinner"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <IonIcon icon={paperPlaneOutline} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
    </div>
  );
};

export default ContactPage;
